import { registerOverlay } from "./overlay-back";
import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import type { ComputerConnection, ConnState, ConnectionSnapshot, Hub, ComputerRecord } from "@foreman/app-core";
import type { Services } from "./services";
import type { Nav } from "./nav";
import type { OfficeAgent } from "@foreman/protocol";
import { deskOf, type Desk } from "./team-world/look";

// ---- context & hooks ---------------------------------------------------------------

interface AppValue extends Services {
  toast: (message: string) => void;
}
export const AppContext = createContext<AppValue | null>(null);
export function useApp(): AppValue {
  const v = useContext(AppContext);
  if (!v) throw new Error("AppContext missing");
  return v;
}

export function useHubSummaries(hub: Hub) {
  return useSyncExternalStore(hub.subscribe, hub.getSnapshot);
}

const noopSubscribe = () => () => {};
const EMPTY: ConnectionSnapshot = { state: "connecting", sessions: [], sessionsStatus: "loading", sessionsUpdatedAt: null, devices: [], devicesStatus: "idle", inbox: [], inboxLoaded: false, paused: false, account: null, blocked: null, power: null, powerUnsupported: false, powerError: null };
const emptySnapshot = () => EMPTY;

/** Live view of one computer's connection. `conn` is undefined once the computer has been removed. */
export function useConnection(computerId: string): { conn: ComputerConnection | undefined; snap: ConnectionSnapshot; computer: ComputerRecord | undefined } {
  const { hub, store } = useApp();
  useHubSummaries(hub); // re-render if the connection object itself is replaced (re-pairing, removal)
  const conn = hub.get(computerId);
  const snap = useSyncExternalStore(conn?.subscribe ?? noopSubscribe, conn?.getSnapshot ?? emptySnapshot);
  return { conn, snap, computer: store.get(computerId) };
}

/**
 * The "Check now" action for a computer that looks offline: reconnects, shows progress, and says what came of it, so a tap
 * never looks dead. Use `checking` to show a spinner and disable the button.
 */
export function useCheckNow(): (conn: ComputerConnection | undefined) => Promise<void> {
  const { toast } = useApp();
  return useCallback(
    async (conn) => {
      if (!conn || conn.getSnapshot().checking) return;
      const r = await conn.checkNow();
      toast(r === "online" ? "Connected" : r === "offline" ? "Still offline. Open Brocoded on that computer and make sure it has internet." : "No answer yet. Trying again in the background.");
    },
    [toast],
  );
}

/** A text button that checks a computer now, following its own connection so the label shows progress. */
export function CheckNowButton({ computerId }: { computerId: string }) {
  const { conn, snap } = useConnection(computerId);
  const checkNow = useCheckNow();
  return (
    <button className="text-btn" disabled={!!snap.checking} onClick={() => void checkNow(conn)}>
      {snap.checking ? "Checking…" : "Check now"}
    </button>
  );
}

export const STATUS: Record<ConnState, { color: string; text: string }> = {
  ready: { color: "var(--ok)", text: "Connected" },
  connecting: { color: "var(--warn)", text: "Connecting…" },
  reconnecting: { color: "var(--warn)", text: "Reconnecting…" },
  offline: { color: "var(--fg3)", text: "Offline" },
  unauthorized: { color: "var(--danger)", text: "Not authorized" },
};

// ---- small pieces ------------------------------------------------------------------

export function Spinner({ size = 14, style }: { size?: number; style?: CSSProperties }) {
  return <span className="spinner" style={{ width: size, height: size, ...style }} aria-hidden />;
}


const MASCOT_SVG = `<ellipse id="m-shadow" cx="86" cy="181" rx="32" ry="5" fill="#000" style="transform-origin:86px 181px;"/><g id="m-body" style=""><g id="m-flame" style="display:none"><path d="M34 145Q43 190 52 145Z" fill="#ededed"/><path d="M39 145Q43 170 47 145Z" fill="#fff"/><circle cx="36" cy="162" r="1.6" fill="#a1a1a1" style=""/><circle cx="50" cy="166" r="1.4" fill="#a1a1a1" style=""/><circle cx="43" cy="174" r="1.8" fill="#a1a1a1" style=""/></g><path d="M38 86V60" stroke="#a1a1a1" stroke-width="3.5" stroke-linecap="round"/><rect x="35" y="66" width="6" height="8" rx="2" fill="#8f8f8f"/><circle cx="38" cy="57" r="3.6" fill="#00ca50" style=""/><rect x="26" y="84" width="36" height="56" rx="9" fill="#8f8f8f"/><rect x="31" y="90" width="22" height="38" rx="6" fill="#ededed"/><rect x="33.5" y="93" width="5" height="30" rx="2.5" fill="#fff" opacity=".22"/><path d="M33 139h20l-3 7H36z" fill="#2e2e2e"/><g id="m-armB" style="transform-origin:64px 104px;transition:transform .35s"><path d="M64 104L62 122" stroke="#5c5c5c" stroke-width="14" stroke-linecap="round"/><g id="m-foreB" style="transform-origin:62px 122px;transition:transform .35s"><path d="M62 122L61 135" stroke="#5c5c5c" stroke-width="13" stroke-linecap="round"/><circle cx="62" cy="122" r="7" fill="#2e2e2e"/><rect x="52" y="134" width="18" height="6" rx="3" fill="#2e2e2e"/><rect x="52" y="139" width="17" height="13" rx="6.5" fill="#5c5c5c"/></g></g><g id="m-legL" style="transform-origin:72px 148px"><path d="M72 146V166" stroke="#5c5c5c" stroke-width="16" stroke-linecap="round"/><rect x="63" y="154" width="18" height="5" rx="2.5" fill="#2e2e2e"/><path d="M62 166h20a6 6 0 0 1 6 6v2a4 4 0 0 1-4 4H66a4 4 0 0 1-4-4z" fill="#5c5c5c"/><rect x="64" y="176.5" width="22" height="2.5" rx="1.25" fill="#ededed"/></g><g id="m-legR" style="transform-origin:92px 148px"><path d="M92 146V166" stroke="#c4c4c4" stroke-width="16" stroke-linecap="round"/><rect x="83" y="154" width="18" height="5" rx="2.5" fill="#8f8f8f"/><path d="M82 166h21a6 6 0 0 1 6 6v2a4 4 0 0 1-4 4H86a4 4 0 0 1-4-4z" fill="#ededed"/><rect x="84" y="176.5" width="23" height="2.5" rx="1.25" fill="#ededed"/></g><path d="M62 136H106Q108 152 96 154H72Q60 152 62 136Z" fill="#5c5c5c"/><path d="M58 104Q58 90 72 89H96Q110 90 110 104L107 132Q106 142 96 142H70Q60 142 59 132Z" fill="#ededed"/><path d="M64 108q3-9 12-11" stroke="#fff" stroke-opacity=".35" stroke-width="3" stroke-linecap="round" fill="none"/><path d="M64 96L102 136" stroke="#8f8f8f" stroke-width="5" stroke-linecap="round"/><rect x="59" y="134" width="50" height="7" rx="3.5" fill="#8f8f8f"/><rect x="79.5" y="133" width="9" height="9" rx="2" fill="#2e2e2e" stroke="#a1a1a1" stroke-width="1.5"/><circle cx="94" cy="114" r="9.5" fill="#000" stroke="#ededed" stroke-width="2"/><path d="M90 110.5l3.5 3.5-3.5 3.5" stroke="#ededed" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/><rect x="95" y="116.5" width="4.5" height="2" rx="1" fill="#ededed"/><ellipse cx="82" cy="93" rx="20" ry="6" fill="#8f8f8f"/><g id="m-head" style="transform-origin:82px 92px;"><path d="M56 26L52 8l14 12z" fill="#8f8f8f"/><circle cx="80" cy="56" r="42" fill="#ededed"/><ellipse cx="62" cy="30" rx="10" ry="6" fill="#fff" opacity=".28" transform="rotate(-30 62 30)"/><ellipse cx="41" cy="60" rx="7" ry="15" fill="#8f8f8f"/><ellipse cx="40" cy="60" rx="3" ry="8" fill="#ededed" opacity=".85"/><ellipse cx="91" cy="53" rx="27" ry="26" fill="#000" stroke="#ededed" stroke-width="2.2"/><g style="transform-origin:97px 55px;"><rect x="86" y="48" width="6.5" height="13" rx="3.25" fill="#ededed"/><rect x="102" y="48" width="6.5" height="13" rx="3.25" fill="#ededed"/></g><path d="M72 40q8-11 22-11" stroke="#fff" stroke-opacity=".7" stroke-width="4" stroke-linecap="round" fill="none"/><circle cx="110" cy="67" r="2.5" fill="#fff" opacity=".4"/></g><g id="m-arm" style="transform-origin:102px 104px;transform:rotate(-4deg)"><path d="M102 104L104 122" stroke="#c4c4c4" stroke-width="15" stroke-linecap="round"/><g id="m-fore" style="transform-origin:104px 122px;transition:transform .35s"><path d="M104 122L105 135" stroke="#c4c4c4" stroke-width="14" stroke-linecap="round"/><circle cx="104" cy="122" r="7.5" fill="#8f8f8f"/><rect x="95" y="134" width="20" height="6" rx="3" fill="#8f8f8f"/><rect x="95" y="139" width="19" height="15" rx="7" fill="#c4c4c4"/><circle cx="114" cy="143" r="4.5" fill="#a1a1a1"/><path d="M99 146h10" stroke="#5c5c5c" stroke-width="1.4" stroke-linecap="round"/></g><circle cx="102" cy="104" r="10" fill="#8f8f8f"/><circle cx="99" cy="101" r="3" fill="#fff" opacity=".45"/></g></g>`;
/** The astronaut facing you, as on the website (apps/website/public/assets/mascot.svg). */
const MASCOT_FRONT_SVG = `<ellipse cx="80" cy="182" rx="30" ry="4.5" fill="#000"/><g><rect x="46" y="98" width="68" height="54" rx="20" fill="#8f8f8f"/><rect x="46" y="98" width="68" height="54" rx="20" fill="#000" opacity=".18"/><g><path d="M57 114L53 134" stroke="#5c5c5c" stroke-width="13" stroke-linecap="round"/><g><rect x="46" y="132" width="14" height="6" rx="3" fill="#2e2e2e"/><circle cx="53" cy="142" r="8" fill="#8f8f8f"/></g></g><g><path d="M70 148V162" stroke="#5c5c5c" stroke-width="15" stroke-linecap="round"/><path d="M60 160h20a5 5 0 0 1 5 5v3a4 4 0 0 1-4 4H62a4 4 0 0 1-4-4v-3a5 5 0 0 1 2-5z" fill="#8f8f8f"/></g><g><path d="M90 148V162" stroke="#c4c4c4" stroke-width="15" stroke-linecap="round"/><path d="M80 160h20a5 5 0 0 1 5 5v3a4 4 0 0 1-4 4H82a4 4 0 0 1-4-4v-3a5 5 0 0 1 2-5z" fill="#8f8f8f"/></g><rect x="52" y="100" width="56" height="50" rx="22" fill="#ededed"/><path d="M60 112q3-9 11-11" stroke="#fff" stroke-opacity=".4" stroke-width="3" stroke-linecap="round" fill="none"/><rect x="64" y="116" width="32" height="20" rx="7" fill="#000" stroke="#ededed" stroke-width="1.6"/><path d="M71 122l5 4-5 4" stroke="#ededed" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/><rect x="79" y="129.5" width="8" height="2.2" rx="1.1" fill="#ededed"/><rect x="52" y="141" width="56" height="6" rx="3" fill="#8f8f8f"/><ellipse cx="80" cy="97" rx="26" ry="7" fill="#8f8f8f"/><g><path d="M80 17V8" stroke="#a1a1a1" stroke-width="3" stroke-linecap="round"/><circle cx="80" cy="6" r="4" fill="#00ca50"/><circle cx="80" cy="58" r="43" fill="#ededed"/><ellipse cx="58" cy="30" rx="11" ry="6" fill="#fff" opacity=".28" transform="rotate(-30 58 30)"/><ellipse cx="38" cy="60" rx="7" ry="14" fill="#8f8f8f"/><ellipse cx="122" cy="60" rx="7" ry="14" fill="#8f8f8f"/><ellipse cx="37.5" cy="60" rx="3" ry="7" fill="#ededed" opacity=".85"/><ellipse cx="122.5" cy="60" rx="3" ry="7" fill="#ededed" opacity=".85"/><ellipse cx="80" cy="57" rx="31" ry="27" fill="#000" stroke="#ededed" stroke-width="2.2"/><g><rect x="64" y="46" width="7" height="14" rx="3.5" fill="#ededed"/><rect x="89" y="46" width="7" height="14" rx="3.5" fill="#ededed"/></g><path d="M72 68q8 6 16 0" stroke="#ededed" stroke-width="2.4" stroke-linecap="round" fill="none"/><path d="M62 40q8-10 22-10" stroke="#fff" stroke-opacity=".6" stroke-width="4" stroke-linecap="round" fill="none"/></g><g><path d="M103 114L107 134" stroke="#c4c4c4" stroke-width="14" stroke-linecap="round"/><g><rect x="100" y="132" width="14" height="6" rx="3" fill="#8f8f8f"/><circle cx="107" cy="142" r="8.5" fill="#8f8f8f"/><circle cx="104.5" cy="139.5" r="2.4" fill="#fff" opacity=".5"/></g></g></g>`;
/** The Brocoded astronaut (side-on, or `front` facing you). Decorative: it never carries information the text next to it doesn't. */
export function Mascot({ size = 96, front = true }: { size?: number; front?: boolean }) {
  return (
    <svg width={size} height={Math.round((size * 190) / 160)} viewBox="0 0 160 190" aria-hidden dangerouslySetInnerHTML={{ __html: front ? MASCOT_FRONT_SVG : MASCOT_SVG }} />
  );
}
const MARK_SVG = `<path d="M14 7.4V5.6" stroke="#8f8f8f" stroke-width="1.4" stroke-linecap="round"/><circle cx="14" cy="4.4" r="1.6" fill="#00ca50"/><ellipse cx="4" cy="18.4" rx="1.6" ry="3.2" fill="#8f8f8f"/><circle cx="15" cy="18" r="11.2" fill="#ededed"/><ellipse cx="18.6" cy="17.6" rx="7" ry="6.2" fill="#000"/><rect x="16.2" y="14.6" width="2.1" height="4.2" rx="1.05" fill="#ededed"/><rect x="21.2" y="14.6" width="2.1" height="4.2" rx="1.05" fill="#ededed"/><path d="M17.4 21.2q2.3 1.8 4.6 0" stroke="#ededed" stroke-width="1.2" stroke-linecap="round" fill="none"/><path d="M27.2 3.8000000000000003Q27.2 6.2 29.599999999999998 6.2Q27.2 6.2 27.2 8.6Q27.2 6.2 24.8 6.2Q27.2 6.2 27.2 3.8000000000000003Z" fill="#ededed" opacity="1"/><path d="M4.6 25.3Q4.6 27.2 6.5 27.2Q4.6 27.2 4.6 29.099999999999998Q4.6 27.2 2.6999999999999997 27.2Q4.6 27.2 4.6 25.3Z" fill="#a1a1a1" opacity="1"/><path d="M28.2 24.6Q28.2 26 29.599999999999998 26Q28.2 26 28.2 27.4Q28.2 26 26.8 26Q28.2 26 28.2 24.6Z" fill="#8f8f8f" opacity="1"/><path d="M8.2 4.1Q8.2 5.2 9.299999999999999 5.2Q8.2 5.2 8.2 6.300000000000001Q8.2 5.2 7.1 5.2Q8.2 5.2 8.2 4.1Z" fill="#8f8f8f" opacity="0.8"/>`;
export const Logo = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden dangerouslySetInnerHTML={{ __html: MARK_SVG }} />
);

/** The Claude, Codex and Antigravity marks, so a chat reads as theirs. `key` is `agentOf(...).key`; anything else gets a terminal prompt. */
export function AgentLogo({ agent, size = 20 }: { agent: "claude" | "codex" | "antigravity" | "other"; size?: number }) {
  if (agent === "antigravity") {
    // the Antigravity arch, with its blue, red, yellow and green as one soft gradient (the original blurs blobs, too heavy for a 14px icon)
    return (
      <svg width={size} height={size} viewBox="0 10 206 190" aria-hidden style={{ flex: "none" }}>
        <defs>
          <linearGradient id="agy-g" x1="20" y1="185" x2="190" y2="20" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#00b95c" />
            <stop offset=".35" stopColor="#3186ff" />
            <stop offset=".7" stopColor="#fc413d" />
            <stop offset="1" stopColor="#ffe432" />
          </linearGradient>
        </defs>
        <path d="M175.277 181.636C185.19 189.071 200.06 184.115 186.429 170.484C145.536 130.83 154.211 21.7824 103.404 21.7824C52.5979 21.7824 61.2722 130.83 20.3793 170.484C5.50915 185.354 21.6185 189.071 31.5319 181.636C69.9464 155.614 67.4681 109.764 103.404 109.764C139.34 109.764 136.862 155.614 175.277 181.636Z" fill="url(#agy-g)" />
      </svg>
    );
  }
  if (agent === "claude") {
    // the Claude spark, in Claude's clay
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="#d97757" aria-hidden style={{ flex: "none" }}>
        <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z" />
      </svg>
    );
  }
  if (agent === "codex") {
    // a cloud holding a terminal prompt, the Codex icon
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 17.9 8.6 4.8 4.8 0 0 1 17 18z" />
        <path d="m9.5 11.5 2.2 2-2.2 2M13.2 15.5h2.4" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m6 8 4 4-4 4M12.5 16H18" />
    </svg>
  );
}

/** An agent's name in running text, with its mark in front. (Demo edit: safe to delete this note.) */
export function AgentName({ agent, children }: { agent: "claude" | "codex" | "antigravity"; children?: ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "baseline", whiteSpace: "nowrap" }}>
      <AgentLogo agent={agent} size={14} />
      {children ?? (agent === "claude" ? "Claude Code" : agent === "codex" ? "Codex" : "Antigravity")}
    </span>
  );
}

const svg = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" } as const;
export const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="2">
    <path d="M14 4l-7 7 7 7" />
  </svg>
);
export const MoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
    <circle cx="4" cy="10" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="16" cy="10" r="1.6" />
  </svg>
);
export const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" {...svg} strokeWidth="1.8">
    <path d="M16 10a6 6 0 1 1-1.8-4.3" />
    <path d="M16 3.5v3.2h-3.2" />
  </svg>
);
export const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" {...svg} strokeWidth="2">
    <path d="M8 2v12M2 8h12" />
  </svg>
);
export const InboxIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.8">
    <path d="M3 12.5 5.6 4.8A1.5 1.5 0 0 1 7 3.8h8a1.5 1.5 0 0 1 1.4 1L19 12.5" />
    <path d="M3 12.5v4a1.7 1.7 0 0 0 1.7 1.7h12.6A1.7 1.7 0 0 0 19 16.5v-4h-4.2a1.6 1.6 0 0 0-1.5 1.1 2.4 2.4 0 0 1-4.6 0A1.6 1.6 0 0 0 7.2 12.5H3Z" />
  </svg>
);
export const ComputerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.7">
    <rect x="2.5" y="3.5" width="17" height="11.5" rx="2" />
    <path d="M8 19h6M11 15v4" />
  </svg>
);
export const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" {...svg} strokeWidth="2">
    <path d="m6 3 5 5-5 5" />
  </svg>
);
/** The tick inside a selected radio: a clean 2px check, centred, drawn for a 20px circle. */
export const RadioTick = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2.4 6.4 4.8 8.8 9.6 3.4" />
  </svg>
);

export const CheckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 26 26" {...svg} strokeWidth="2.6">
    <path d="M6 13.5l4.5 4.5L20 8.5" />
  </svg>
);
export const ComposeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" {...svg} strokeWidth="1.8">
    <path d="M3 5h12M3 9h12M3 13h7" />
  </svg>
);

export const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.7">
    <path d="M4 6h14M4 11h14M4 16h9" />
  </svg>
);
export const TabInboxIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.7">
    <path d="m3.3 12.4 2.6-6.8A1.5 1.5 0 0 1 7.300 4.600h7.400a1.500 1.500 0 0 1 1.400 1l2.600 6.800v4.100a1.700 1.700 0 0 1-1.700 1.700H5a1.700 1.700 0 0 1-1.700-1.700z" />
    <path d="M3.300 12.400h4.300a3.400 3.400 0 0 0 6.800 0h4.300" />
  </svg>
);
export const TabComputerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.7">
    <rect x="2.500" y="3.500" width="17" height="11.500" rx="2.200" />
    <path d="M7.500 19h7M11 15v4" />
  </svg>
);
export const UserIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.7">
    <circle cx="11" cy="7.500" r="3.500" />
    <path d="M4.200 18.400c.9-3.400 3.600-5 6.800-5s5.900 1.600 6.800 5" />
  </svg>
);

export const SettingsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" {...svg} strokeWidth="1.7">
    <path d="M3.500 6.500h8M15.500 6.500h3M3.500 15.500h3M10.500 15.500h8" />
    <circle cx="13.500" cy="6.500" r="2" />
    <circle cx="8.500" cy="15.500" r="2" />
  </svg>
);

/** What the Teams tab should say: untouched ("new"), someone needs you, or the team is working. Also keeps the team picture fresh for the first ready computer. */
export function useTeamsBadge(): { kind: "new" | "attn" | "live" | "none"; agent?: OfficeAgent; desk?: Desk; computerId?: string; need?: string } {
  const { hub } = useApp();
  const summaries = useHubSummaries(hub);
  const id = summaries.find((s) => s.state === "ready")?.computer.id ?? "";
  const { conn, snap } = useConnection(id);
  const ready = snap.state === "ready";
  useEffect(() => {
    if (!ready || !conn) return;
    conn.refreshOffice();
    const t = setInterval(() => conn.refreshOffice(), 20_000);
    return () => clearInterval(t);
  }, [ready, conn]);
  const office = snap.office;
  let visited = true;
  try {
    visited = localStorage.getItem("foreman.teams.visited") === "1";
  } catch {
    /* private mode: treat as visited, so a badge never nags */
  }
  // No team engine on this computer: nothing to point at, so no badge (a NEW pill would lead to an empty room).
  if (!office?.available) return { kind: "none" };
  const live = snap.officeActivity ?? {};
  const desks = office.agents.filter((a) => !a.isGod && !a.archived).map((a) => ({ a, d: deskOf(a, { latest: live[a.id]?.[0], tasks: office.tasks, agents: office.agents }) }));
  const attn = desks.find((x) => x.d.state === "wait" || x.d.state === "fail" || x.d.state === "stopped");
  if (attn) {
    const waiting = live[attn.a.id]?.find((e) => e.kind === "waiting");
    return { kind: "attn", agent: attn.a, desk: attn.d, computerId: id, need: attn.d.state === "wait" ? (waiting?.detail?.slice(0, 120) ?? `${attn.a.name} is waiting for you`) : attn.d.state === "stopped" ? `${attn.a.name} was stopped` : `${attn.a.name} hit an error` };
  }
  // Untouched beats busy: the first visit is what the NEW pill is for.
  if (!visited) return { kind: "new" };
  if (desks.some((x) => x.d.state === "work")) return { kind: "live" };
  return { kind: "none" };
}

const TABS = [
  { name: "home", label: "Home", icon: <HomeIcon /> },
  { name: "inbox", label: "Activity", icon: <TabInboxIcon /> },
  // Teams tab hidden for now (re-enable by uncommenting):
  // { name: "teams", label: "Teams", icon: <span className="tw-tabicon" aria-hidden><i /><i /><i /></span> },
  { name: "computers", label: "Computers", icon: <TabComputerIcon /> },
  { name: "account", label: "Settings", icon: <SettingsIcon /> },
] as const;

/** The bottom bar on the four root screens. Tapping a tab swaps the root screen; the back gesture still leaves the app from any of them. */
export function TabBar({ nav }: { nav: Nav }) {
  const { hub } = useApp();
  const waiting = useHubSummaries(hub).reduce((n, s) => n + s.needsYou, 0);
  // const teams = useTeamsBadge(); // Teams hidden for now
  return (
    <nav className="tabbar" aria-label="Main">
      {TABS.map((t) => {
        const on = nav.view.name === t.name;
        const badge = t.name === "inbox" && waiting > 0 ? waiting : 0;
        return (
          <button
            key={t.name}
            className={"tab" + (on ? " on" : "")}
            aria-current={on ? "page" : undefined}
            aria-label={badge ? `${t.label}, ${badge} waiting` : t.label}
            onClick={() => !on && nav.goTab({ name: t.name })}
          >
            <span className="tab-icon">
              {t.icon}
              {badge > 0 && <span className="tab-badge">{badge > 9 ? "9+" : badge}</span>}
            </span>
            {/* Teams hidden for now:
            {t.name === "teams" && teams.kind === "new" && <span className="tw-tabnew">NEW</span>}
            {t.name === "teams" && teams.kind === "attn" && <span className="tw-tabattn" aria-hidden>!</span>}
            {t.name === "teams" && teams.kind === "live" && <span className="tw-tablive" aria-hidden />}
            */}
            <span className="tab-label">{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function BackButton({ onClick, label = "Back" }: { onClick: () => void; label?: string }) {
  return (
    <button className="icon-btn fg" aria-label={label} onClick={onClick}>
      <BackIcon />
    </button>
  );
}

/** Colored banner under a header (connecting / reconnecting / offline / removed). */
export function Banner(props: { title: string; sub?: string; bg: string; iconColor: string; spin?: boolean; dot?: boolean; children?: ReactNode }) {
  return (
    <div className="banner-wrap">
      <div className="banner" style={{ background: props.bg }} role="status">
        <div className="icon" style={{ color: props.iconColor }}>
          {props.spin && <Spinner />}
          {props.dot && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "currentColor", display: "block" }} />}
        </div>
        <div className="body">
          <div className="t">{props.title}</div>
          {props.sub && <div className="s">{props.sub}</div>}
          {props.children}
        </div>
      </div>
    </div>
  );
}

/**
 * A whole screen laid over the app for a short task (start a session, pick a folder): back arrow and title on top, a body that
 * scrolls, and one action pinned to the bottom above the home bar. Content is held to a comfortable width on big screens.
 * `top` stays put under the header (a search box, a path), so it is still there in a long list.
 */
export function FullScreenSheet({ title, sub, onBack, top, footer, children }: { title: string; sub?: string; onBack: () => void; top?: ReactNode; footer?: ReactNode; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onBack();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);
  return (
    <div className="fs" role="dialog" aria-modal="true" aria-label={title}>
      <div className="safe-top">
        <div className="topbar fs-top">
          <BackButton onClick={onBack} />
          <div className="title-col">
            <div className="t">{title}</div>
            {sub && <div className="sub">{sub}</div>}
          </div>
        </div>
      </div>
      {top && (
        <div className="fs-sticky">
          <div className="fs-inner">{top}</div>
        </div>
      )}
      <div className="fs-body">
        <div className="fs-inner fs-stack">{children}</div>
      </div>
      {footer && (
        <div className="fs-foot">
          <div className="fs-inner">{footer}</div>
        </div>
      )}
    </div>
  );
}

/** `top` lifts the sheet above full-screen sheets (the sign-in ask can appear over the New session screen). */
/** `center`: a modal in the middle of the screen instead of a sheet from the bottom (used for the guest limit). */
export function Sheet({ onClose, children, dim = true, top = false, center = false, tall = false }: { onClose: () => void; children: ReactNode; dim?: boolean; top?: boolean; center?: boolean; /** Almost the whole height (a panel over the screen behind it, which stays visible at the top). */ tall?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div className={"scrim" + (dim ? " dim" : "") + (top ? " top" : "")} onClick={onClose} />
      <div className={"sheet-wrap" + (top ? " top" : "") + (center ? " center" : "") + (tall ? " tall" : "")}>
        <div className="sheet" role="dialog" aria-modal="true">
          {!center && <div className="grab" />}
          {children}
        </div>
      </div>
    </>
  );
}

/** Popover menu anchored under the header's overflow button. */
export function Menu({ onClose, children, wide }: { onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="menu-anchor">
        <div className="inner" style={{ maxWidth: wide ? "none" : 720 }}>
          <div className="menu" role="menu">
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

export function useToast(): { message: string | null; show: (m: string) => void } {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const show = useCallback((m: string) => {
    setMessage(m);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), 1800);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { message, show };
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="toast-wrap">
      <div className="toast" role="status">
        {message}
      </div>
    </div>
  );
}

/**
 * What to show for a connection state: a link that just dropped keeps showing its last good state for a few seconds, so a blip
 * that heals by itself never flashes "Reconnecting…". Offline and removed show at once, and so does a first connect.
 */
export function useCalmState(state: ConnState, holdMs = 3000): ConnState {
  const [shown, setShown] = useState(state);
  useEffect(() => {
    const blip = shown === "ready" && (state === "reconnecting" || state === "connecting");
    if (!blip) return void setShown(state);
    const t = setTimeout(() => setShown(state), holdMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, holdMs]);
  return shown;
}

/**
 * A notification that slides in under the header while no computer is reachable and at least one is being retried, and goes away
 * when one answers. Held back for three seconds so a quick reconnect never flashes it. Offline and removed computers have their own cards.
 */
export function ReconnectNotice({ hub }: { hub: Hub }) {
  const summaries = useHubSummaries(hub);
  const trying = summaries.length > 0 && summaries.every((s) => s.state !== "ready") && summaries.some((s) => s.state === "connecting" || s.state === "reconnecting");
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!trying) return void setShown(false);
    const t = setTimeout(() => setShown(true), 3000);
    return () => clearTimeout(t);
  }, [trying]);
  if (!trying || !shown) return null;
  return (
    <div className="reconnect-wrap">
      <div className="reconnect-notice" role="status">
        <Spinner size={13} />
        Reconnecting…
      </div>
    </div>
  );
}

const editable = (el: Element | null) => !!el && el.matches("input, textarea, select, [contenteditable='true']");

/**
 * Tracks the on-screen keyboard through the visual viewport (and keeps the app column above it).
 *
 * "Open" means the visible height fell well below the tallest it has been at this width AND something you can type into
 * has focus. Comparing with `window.innerHeight` alone does not work on Android, where the page itself shrinks with the
 * keyboard (`interactive-widget=resizes-content`) and the two heights stay equal. Rotating resets the baseline.
 */
/**
 * Lets the phone's back gesture (or the browser's Back) close an overlay instead of leaving the screen under it (see overlay-back.ts).
 * Only for overlays that never navigate somewhere else while open.
 */
export function useBackToClose(active: boolean, onClose: () => void): void {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => (active ? registerOverlay(() => close.current()) : undefined), [active]);
}

export function useViewport(): { keyboardOpen: boolean } {
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    const touch = window.matchMedia("(pointer: coarse)");
    let tallest = 0;
    let width = 0;
    const apply = () => {
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty("--app-h", `${h}px`);
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        tallest = 0; // rotated or resized: what "tall" means has changed
      }
      tallest = Math.max(tallest, window.innerHeight, h);
      setKeyboardOpen(touch.matches && tallest - h > 120 && editable(document.activeElement));
      if (vv && vv.offsetTop > 0) window.scrollTo(0, 0); // iOS scrolls the page when the keyboard opens
    };
    apply();
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    document.addEventListener("focusin", apply);
    document.addEventListener("focusout", apply);
    return () => {
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      document.removeEventListener("focusin", apply);
      document.removeEventListener("focusout", apply);
    };
  }, []);
  return { keyboardOpen };
}

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error("timed out")), ms))]);

/** Copy to the clipboard. The async API can hang when the page isn't focused, so it times out and falls back to the legacy command. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await withTimeout(navigator.clipboard.writeText(text), 2000);
    return true;
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

export async function readClipboard(): Promise<string | null> {
  try {
    return await withTimeout(navigator.clipboard.readText(), 2000);
  } catch {
    return null;
  }
}
